import Phaser from 'phaser';
import {
  COLORS,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  HISTORIKK_GRAF_DAGER,
  LAV_KAPITAL_GRENSE,
  SJANGER_FALLBACK_FARGE,
  SJANGER_FARGER,
  fontStr,
} from '@/config';
import { gameState } from '@/state/store';
import { snittTerninger } from '@/systems/tier';
import {
  APP_VERSION,
  lesCacheTidspunkt,
  sjekkForOppdatering,
} from '@/pwa/registrerSw';
import {
  abonnerInstallTilgjengelig,
  erInstallert,
  triggerInstallPrompt,
} from '@/pwa/installasjon';
import { abonnerAdmin, erAdminAktivert } from '@/state/adminMode';
import {
  DAGER_PER_AAR,
  SESONG_BESKRIVELSE,
  SESONG_NAVN,
  dagIAaret,
  sesongForDag,
  type Sesong,
} from '@/systems/sesong';
import { KATEGORI_NAVN } from '@/systems/brage';
import { PRIS_KATALOG, type PrisId } from '@/systems/priser';
import type { FilmRettighet } from '@/systems/lydbokOgFilm';

const PANEL_WIDTH = 360;

type PrisArkivFilter = 'alle' | 'brage' | PrisId;

export type DireksjonDashboardCallbacks = {
  onAapneMinKarriere?: () => void;
  onAapneAdmin?: () => void;
};

export class DireksjonDashboard {
  private layer?: Phaser.GameObjects.Container;
  private unsubscribe?: () => void;
  private unsubscribeAdmin?: () => void;
  private scrollY = 0;
  private scrollMax = 0;
  private innholdsContainer?: Phaser.GameObjects.Container;
  private mask?: Phaser.Display.Masks.GeometryMask;
  private wheelHandler?: (_p: unknown, _g: unknown, _dx: number, dy: number) => void;
  private prisFilter: PrisArkivFilter = 'alle';

  constructor(
    private scene: Phaser.Scene,
    private callbacks: DireksjonDashboardCallbacks = {},
  ) {}

  get isOpen(): boolean {
    return !!this.layer;
  }

  open(): void {
    if (this.layer) return;
    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    this.layer = this.scene.add.container(0, 0).setDepth(15);

    const bg = this.scene.add
      .rectangle(0, 0, roomWidth, GAME_HEIGHT, COLORS.beige, 0.92)
      .setOrigin(0, 0);
    this.layer.add(bg);

    this.wheelHandler = (
      _p: unknown,
      _g: unknown,
      _dx: number,
      dy: number,
    ) => {
      this.scrollBy(dy);
    };
    this.scene.input.on('wheel', this.wheelHandler);

    bg.setInteractive();
    bg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const startY = pointer.y;
      const startScroll = this.scrollY;
      const onMove = (p: Phaser.Input.Pointer) => {
        const dy = p.y - startY;
        this.scrollY = Math.max(0, Math.min(this.scrollMax, startScroll - dy));
        if (this.innholdsContainer) this.innholdsContainer.y = -this.scrollY;
      };
      const onUp = () => {
        this.scene.input.off('pointermove', onMove);
        this.scene.input.off('pointerup', onUp);
      };
      this.scene.input.on('pointermove', onMove);
      this.scene.input.on('pointerup', onUp);
    });

    this.render();
    this.unsubscribe = gameState.subscribe(() => this.render());
    this.unsubscribeAdmin = abonnerAdmin(() => this.render());
  }

  close(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.unsubscribeAdmin?.();
    this.unsubscribeAdmin = undefined;
    if (this.wheelHandler) {
      this.scene.input.off('wheel', this.wheelHandler);
      this.wheelHandler = undefined;
    }
    this.layer?.destroy(true);
    this.layer = undefined;
    this.innholdsContainer = undefined;
    this.mask = undefined;
    this.scrollY = 0;
    this.scrollMax = 0;
  }

  private scrollBy(dy: number): void {
    this.scrollY = Math.max(0, Math.min(this.scrollMax, this.scrollY + dy));
    if (this.innholdsContainer) this.innholdsContainer.y = -this.scrollY;
  }

  private render(): void {
    if (!this.layer) return;
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }

    const roomWidth = GAME_WIDTH - PANEL_WIDTH;
    const padding = 40;
    const innholdsBredde = roomWidth - padding * 2;

    const maskShape = this.scene.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(0, 0, roomWidth, GAME_HEIGHT);
    this.mask = maskShape.createGeometryMask();

    const innhold = this.scene.add.container(padding, 0);
    innhold.setMask(this.mask);
    this.layer.add(innhold);
    this.innholdsContainer = innhold;

    let y = 32;

    y = this.renderHeader(innhold, innholdsBredde, y);
    y += 12;

    const snap = gameState.snapshot;
    if (snap.kroner < LAV_KAPITAL_GRENSE) {
      y = this.renderRoedt(innhold, innholdsBredde, y, snap.kroner);
      y += 16;
    }

    y = this.renderKpiCards(innhold, innholdsBredde, y);
    y += 24;

    y = this.renderMinKarriere(innhold, innholdsBredde, y);
    y += 24;

    if (gameState.harDashboardSesongPrognose()) {
      y = this.renderSesongPrognose(innhold, innholdsBredde, y);
      y += 24;
    }

    y = this.renderGraf(innhold, innholdsBredde, y);
    y += 24;

    y = this.renderTopp10(innhold, innholdsBredde, y);
    y += 24;

    y = this.renderSjangerFordeling(innhold, innholdsBredde, y);
    y += 40;

    y = this.renderPrisArkiv(innhold, innholdsBredde, y);
    y += 40;

    y = this.renderInternasjonaleAvtaler(innhold, innholdsBredde, y);
    y += 40;

    y = this.renderLydbokOgFilm(innhold, innholdsBredde, y);
    y += 40;

    y = this.renderCalidrisAvkortninger(innhold, innholdsBredde, y);
    y += 40;

    y = this.renderAppInfoSeksjon(innhold, innholdsBredde, y);
    y += 40;

    y = this.renderResetSeksjon(innhold, innholdsBredde, y);
    y += 40;

    const synligHoyde = GAME_HEIGHT - 60;
    this.scrollMax = Math.max(0, y - synligHoyde);
  }

  private renderMinKarriere(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const sf = gameState.spillerForfatter;
    const snitt = beregnStatSnitt(sf.stats);
    const maal = 30;

    const overskrift = this.scene.add
      .text(0, y, 'MIN KARRIERE', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(overskrift);
    y += 38;

    const kortH = 168;
    // Drop-shadow for depth (runde 37)
    const shadow = this.scene.add
      .rectangle(3, y + 5, width, kortH, 0x000000, 0.22)
      .setOrigin(0, 0);
    layer.add(shadow);
    const bg = this.scene.add
      .rectangle(0, y, width, kortH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 0.4);
    layer.add(bg);

    const venstreBorder = this.scene.add
      .rectangle(0, y, 4, kortH, COLORS.wine, 1)
      .setOrigin(0, 0);
    layer.add(venstreBorder);

    // Ikon-illustrasjon: penn + ark som dekorativ aksent
    const ikon = this.scene.add
      .text(24, y + 18, '✍️', {
        fontFamily: FONTS.display,
        fontSize: fontStr(28),
      })
      .setOrigin(0, 0);
    layer.add(ikon);

    const fornavn = (sf.navn.trim() || 'Sven').split(' ')[0];
    const navn = this.scene.add
      .text(72, y + 16, `${fornavn} – aspirerende forfatter`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#862736',
        wordWrap: { width: width - 296 },
      })
      .setOrigin(0, 0);
    layer.add(navn);

    const meta = this.scene.add
      .text(
        20,
        y + 68,
        `Stat-snitt ${snitt.toFixed(1)} · Skrivelab-bidrag ${sf.skrivelabBidrag} / ${maal}`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(16),
          color: '#3a2e22',
          fontStyle: 'italic',
          wordWrap: { width: width - 240 },
        },
      )
      .setOrigin(0, 0);
    layer.add(meta);

    if (sf.publiseringsklarErklart) {
      const badge = this.scene.add
        .text(20, y + 110, '✓ Publiseringsklar — runde 28 åpner skrive-modulen', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(14),
          color: '#3d5a4d',
          fontStyle: '700',
          wordWrap: { width: width - 240 },
        })
        .setOrigin(0, 0);
      layer.add(badge);
    } else {
      const tips = this.scene.add
        .text(20, y + 110, 'Send tekster i Skrivelab for å bygge stats.', {
          fontFamily: FONTS.body,
          fontSize: fontStr(14),
          color: '#7a7060',
          fontStyle: 'italic',
          wordWrap: { width: width - 240 },
        })
        .setOrigin(0, 0);
      layer.add(tips);
    }

    const knappW = 220;
    const knappH = 66;
    const knappX = width - knappW - 16;
    const knappY = y + (kortH - knappH) / 2;
    const knappShadow = this.scene.add
      .rectangle(knappX + 2, knappY + 4, knappW, knappH, 0x000000, 0.28)
      .setOrigin(0, 0);
    const knappBg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, COLORS.ochre, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 0.6);
    const knappTxt = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, 'Åpne →', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#1a1a1a',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    layer.add([knappShadow, knappBg, knappTxt]);
    knappBg.setInteractive({ useHandCursor: true });
    knappBg.on('pointerover', () => knappBg.setAlpha(0.85));
    knappBg.on('pointerout', () => knappBg.setAlpha(1));
    knappBg.on('pointerup', () => {
      this.callbacks.onAapneMinKarriere?.();
    });

    return y + kortH;
  }

  private renderResetSeksjon(
    layer: Phaser.GameObjects.Container,
      width: number,
      y: number,
  ): number {
    const overskrift = this.scene.add
      .text(0, y, 'Innstillinger', {
        fontFamily: FONTS.display,
        fontSize: fontStr(32),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    layer.add(overskrift);
    y += 50;

    const knappH = 64;
    const bg = this.scene.add
      .rectangle(0, y, width, knappH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 0.8);
    layer.add(bg);

    const tekst = this.scene.add
      .text(width / 2, y + knappH / 2, 'Start på nytt (slett save)', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(24),
        color: '#862736',
        fontStyle: '600',
      })
      .setOrigin(0.5);
    layer.add(tekst);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(COLORS.wine, 0.1));
    bg.on('pointerout', () => bg.setFillStyle(COLORS.white, 1));
    bg.on('pointerup', () => this.bekreftReset());

    return y + knappH;
  }

  private renderAppInfoSeksjon(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const overskrift = this.scene.add
      .text(0, y, 'App', {
        fontFamily: FONTS.display,
        fontSize: fontStr(32),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    layer.add(overskrift);
    y += 50;

    const versjonsTekst = this.formatterVersjonslinje();
    const info = this.scene.add
      .text(0, y, versjonsTekst, {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#3a2e22',
      })
      .setOrigin(0, 0);
    layer.add(info);
    y += 36;

    const kreditt = this.scene.add
      .text(0, y, 'Laget av Indieweb · indieweb.no', {
        fontFamily: FONTS.body,
        fontSize: fontStr(16),
        color: '#862736',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    kreditt.on('pointerup', () => {
      if (typeof window !== 'undefined') {
        window.open('https://indieweb.no', '_blank', 'noopener');
      }
    });
    layer.add(kreditt);
    y += 36;

    if (!erInstallert()) {
      y = this.renderInstallKnapp(layer, width, y);
      y += 12;
    }

    y = this.renderOppdaterKnapp(layer, width, y);
    y += 12;
    y = this.renderFeedbackKnapp(layer, width, y);

    if (erAdminAktivert()) {
      y += 12;
      y = this.renderAdminKnapp(layer, width, y);
    }
    return y;
  }

  private renderAdminKnapp(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const knappH = 56;
    const bg = this.scene.add
      .rectangle(0, y, width, knappH, COLORS.wine, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 1);
    layer.add(bg);

    const tekst = this.scene.add
      .text(width / 2, y + knappH / 2, '⚙  Admin-modus', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    layer.add(tekst);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setAlpha(0.85));
    bg.on('pointerout', () => bg.setAlpha(1));
    bg.on('pointerup', () => this.callbacks.onAapneAdmin?.());

    return y + knappH;
  }

  private renderFeedbackKnapp(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const knappH = 56;
    const bg = this.scene.add
      .rectangle(0, y, width, knappH, COLORS.beige, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 0.6);
    layer.add(bg);

    const tekst = this.scene.add
      .text(width / 2, y + knappH / 2, '📧  Send tilbakemelding', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#862736',
        fontStyle: '600',
      })
      .setOrigin(0.5);
    layer.add(tekst);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(COLORS.yellowWarm, 0.4));
    bg.on('pointerout', () => bg.setFillStyle(COLORS.beige, 1));
    bg.on('pointerup', () => {
      if (typeof window === 'undefined') return;
      const emne = encodeURIComponent('Forlagshuset — tilbakemelding');
      const kropp = encodeURIComponent(
        'Hei Sven,\n\nJeg testet Forlagshuset og vil dele:\n\nDet jeg likte:\n- \n\nDet som forvirret meg:\n- \n\nForslag:\n- \n\nMvh,\n',
      );
      window.location.href = `mailto:sven.inge@forfatterskolen.no?subject=${emne}&body=${kropp}`;
    });

    return y + knappH;
  }

  private renderInstallKnapp(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const knappH = 56;
    const bg = this.scene.add
      .rectangle(0, y, width, knappH, COLORS.ochre, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 0.6);
    layer.add(bg);

    const tekst = this.scene.add
      .text(width / 2, y + knappH / 2, '↓  Installer Forlagshuset', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#862736',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    layer.add(tekst);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(0xc88f3a, 1));
    bg.on('pointerout', () => bg.setFillStyle(COLORS.ochre, 1));
    bg.on('pointerup', () => {
      void triggerInstallPrompt();
    });

    const unsub = abonnerInstallTilgjengelig((tilgjengelig) => {
      bg.setAlpha(tilgjengelig ? 1 : 0.45);
      tekst.setAlpha(tilgjengelig ? 1 : 0.6);
      if (tilgjengelig) bg.setInteractive({ useHandCursor: true });
      else bg.disableInteractive();
    });
    bg.once('destroy', () => unsub());

    return y + knappH;
  }

  private renderOppdaterKnapp(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const knappH = 48;
    const bg = this.scene.add
      .rectangle(0, y, width, knappH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.oakDark, 0.5);
    layer.add(bg);

    const tekst = this.scene.add
      .text(width / 2, y + knappH / 2, 'Sjekk for oppdatering', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: '#3a2e22',
      })
      .setOrigin(0.5);
    layer.add(tekst);

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(0xf0e6d0, 1));
    bg.on('pointerout', () => bg.setFillStyle(COLORS.white, 1));
    bg.on('pointerup', async () => {
      tekst.setText('Sjekker …');
      const ok = await sjekkForOppdatering();
      tekst.setText(ok ? 'Sjekket — last siden på nytt for ny versjon' : 'Ingen service worker aktiv');
    });

    return y + knappH;
  }

  private formatterVersjonslinje(): string {
    const cacheTid = lesCacheTidspunkt();
    if (!cacheTid) return `Forlagshuset v${APP_VERSION} — cache: ikke etablert`;
    const dd = String(cacheTid.getDate()).padStart(2, '0');
    const mm = String(cacheTid.getMonth() + 1).padStart(2, '0');
    const yyyy = cacheTid.getFullYear();
    return `Forlagshuset v${APP_VERSION} — cache oppdatert ${dd}.${mm}.${yyyy}`;
  }

  private bekreftReset(): void {
    if (!this.layer) return;
    const roomWidth = GAME_WIDTH - PANEL_WIDTH;

    const overlay = this.scene.add.container(0, 0).setDepth(40);

    const backdrop = this.scene.add
      .rectangle(0, 0, roomWidth, GAME_HEIGHT, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setInteractive();
    overlay.add(backdrop);

    const dialogW = 520;
    const dialogH = 280;
    const dialogX = (roomWidth - dialogW) / 2;
    const dialogY = (GAME_HEIGHT - dialogH) / 2;

    const dialog = this.scene.add
      .rectangle(dialogX, dialogY, dialogW, dialogH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 1);
    overlay.add(dialog);

    const tittel = this.scene.add
      .text(dialogX + dialogW / 2, dialogY + 40, 'Slett save og start nytt spill?', {
        fontFamily: FONTS.display,
        fontSize: fontStr(26),
        color: '#1a1a1a',
        wordWrap: { width: dialogW - 60 },
        align: 'center',
      })
      .setOrigin(0.5, 0);
    overlay.add(tittel);

    const info = this.scene.add
      .text(
        dialogX + dialogW / 2,
        dialogY + 120,
        'Alle forfattere, bøker, kapital og fremgang blir borte.',
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#6b6b6b',
          wordWrap: { width: dialogW - 60 },
          align: 'center',
        },
      )
      .setOrigin(0.5, 0);
    overlay.add(info);

    const knappH = 56;
    const knappBredde = 200;
    const knappY = dialogY + dialogH - knappH - 24;

    const avbrytBg = this.scene.add
      .rectangle(dialogX + 30, knappY, knappBredde, knappH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.6);
    const avbrytTxt = this.scene.add
      .text(dialogX + 30 + knappBredde / 2, knappY + knappH / 2, 'Avbryt', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#1a1a1a',
      })
      .setOrigin(0.5);
    overlay.add(avbrytBg);
    overlay.add(avbrytTxt);
    avbrytBg.setInteractive({ useHandCursor: true });
    avbrytBg.on('pointerup', () => overlay.destroy(true));

    const bekreftX = dialogX + dialogW - knappBredde - 30;
    const bekreftBg = this.scene.add
      .rectangle(bekreftX, knappY, knappBredde, knappH, COLORS.wine, 1)
      .setOrigin(0, 0);
    const bekreftTxt = this.scene.add
      .text(bekreftX + knappBredde / 2, knappY + knappH / 2, 'Ja, slett alt', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    overlay.add(bekreftBg);
    overlay.add(bekreftTxt);
    bekreftBg.setInteractive({ useHandCursor: true });
    bekreftBg.on('pointerover', () => bekreftBg.setFillStyle(0x6f1f2c));
    bekreftBg.on('pointerout', () => bekreftBg.setFillStyle(COLORS.wine));
    bekreftBg.on('pointerup', () => {
      overlay.destroy(true);
      gameState.nullstill();
      if (typeof window !== 'undefined') {
        window.setTimeout(() => window.location.reload(), 250);
      }
    });
  }

  private renderHeader(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const tittel = this.scene.add
      .text(0, y, 'Direksjon', {
        fontFamily: FONTS.display,
        fontSize: fontStr(64),
        color: '#862736',
      })
      .setOrigin(0, 0);
    layer.add(tittel);

    const undertittel = this.scene.add
      .text(0, y + 78, 'Helhetsbildet av forlaget', {
        fontFamily: FONTS.body,
        fontSize: fontStr(24),
        color: '#3a2e22',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0);
    layer.add(undertittel);
    void width;
    return y + 120;
  }

  private renderRoedt(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
    kroner: number,
  ): number {
    const h = 112;
    const bg = this.scene.add
      .rectangle(0, y, width, h, COLORS.wine, 0.95)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.ink, 0.3);
    layer.add(bg);

    const tekst = this.scene.add
      .text(
        24,
        y + h / 2,
        `⚠ Lav kapital: ${kroner.toLocaleString('nb-NO')} kr — vurder å selge backlist eller pause kampanjer`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(20),
          color: '#ffffff',
          fontStyle: '700',
          wordWrap: { width: width - 48 },
        },
      )
      .setOrigin(0, 0.5);
    layer.add(tekst);
    return y + h;
  }

  private renderSesongPrognose(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const dag = gameState.dag;
    const naa = sesongForDag(dag);
    const neste = nesteSesong(dag);
    const dagerTilNeste = dagerTilSesongSkift(dag);

    const h = 200;
    const bg = this.scene.add
      .rectangle(0, y, width, h, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.ochre, 0.8);
    layer.add(bg);

    const overskrift = this.scene.add
      .text(20, y + 14, 'SESONG-PROGNOSE', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(overskrift);

    const naaText = this.scene.add
      .text(
        20,
        y + 58,
        `${SESONG_NAVN[naa]} (nå) — ${SESONG_BESKRIVELSE[naa]}`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(20),
          color: '#1a1a1a',
          wordWrap: { width: width - 40 },
        },
      )
      .setOrigin(0, 0);
    layer.add(naaText);

    const nesteText = this.scene.add
      .text(
        20,
        y + h - 24,
        `Neste: ${SESONG_NAVN[neste]} om ${dagerTilNeste} ${dagerTilNeste === 1 ? 'dag' : 'dager'}`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: '#862736',
          fontStyle: '700',
        },
      )
      .setOrigin(0, 1);
    layer.add(nesteText);

    return y + h;
  }

  private renderKpiCards(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const totalInntekt = gameState.beregnTotalInntekt();
    const totalUtgift = gameState.beregnTotalUtgift();
    const netto = totalInntekt - totalUtgift;
    const utgivelser = gameState.antallUtgivelser();

    const gap = 16;
    const kortBredde = Math.floor((width - gap * 3) / 4);
    const kortHoyde = 200;

    const kort = [
      {
        label: 'Total inntekt',
        verdi: this.kompakt(totalInntekt),
        farge: '#8b5e3c',
      },
      {
        label: 'Total utgift',
        verdi: this.kompakt(totalUtgift),
        farge: '#862736',
      },
      {
        label: 'Netto',
        verdi: this.kompakt(netto),
        farge: netto >= 0 ? '#3d5a4d' : '#862736',
      },
      {
        label: 'Utgivelser',
        verdi: utgivelser.toLocaleString('nb-NO'),
        farge: '#1a1a1a',
      },
    ];

    for (let i = 0; i < kort.length; i += 1) {
      const x = i * (kortBredde + gap);
      const k = kort[i];

      const bg = this.scene.add
        .rectangle(x, y, kortBredde, kortHoyde, COLORS.white, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(2, COLORS.oakDark, 0.3);
      layer.add(bg);

      const label = this.scene.add
        .text(x + 16, y + 18, k.label.toUpperCase(), {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: '#8b5e3c',
          fontStyle: '700',
          wordWrap: { width: kortBredde - 32 },
        })
        .setOrigin(0, 0);
      layer.add(label);

      const verdi = this.scene.add
        .text(x + 16, y + kortHoyde - 26, k.verdi, {
          fontFamily: FONTS.display,
          fontSize: fontStr(46),
          color: k.farge,
          wordWrap: { width: kortBredde - 32 },
        })
        .setOrigin(0, 1);
      layer.add(verdi);
    }

    return y + kortHoyde;
  }

  private renderGraf(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const h = 320;
    const padding = 56;

    const bg = this.scene.add
      .rectangle(0, y, width, h, COLORS.beige, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.4);
    layer.add(bg);

    const tittel = this.scene.add
      .text(20, y + 16, `KRONER-NIVÅ — SISTE ${HISTORIKK_GRAF_DAGER} DAGER`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(tittel);

    const historikk = gameState.historikk;
    const snittsnitt = historikk.slice(-HISTORIKK_GRAF_DAGER);

    const grafX = padding;
    const grafY = y + 60;
    const grafW = width - padding - 20;
    const grafH = h - 100;

    const g = this.scene.add.graphics();
    g.lineStyle(1, COLORS.ochre, 0.25);
    for (let i = 0; i <= 4; i += 1) {
      const gy = grafY + (grafH * i) / 4;
      g.beginPath();
      g.moveTo(grafX, gy);
      g.lineTo(grafX + grafW, gy);
      g.strokePath();
    }
    layer.add(g);

    if (snittsnitt.length === 0) {
      const tekst = this.scene.add
        .text(width / 2, y + h / 2 + 20, 'Ingen historikk ennå — spill noen dager først', {
          fontFamily: FONTS.body,
          fontSize: fontStr(22),
          color: '#8b5e3c',
          fontStyle: 'italic',
        })
        .setOrigin(0.5);
      layer.add(tekst);
      return y + h;
    }

    const verdier = snittsnitt.map((s) => s.kroner);
    const maks = Math.max(...verdier, 1);
    const min = Math.min(...verdier, 0);
    const span = Math.max(1, maks - min);

    const linje = this.scene.add.graphics();
    linje.lineStyle(3, COLORS.wine, 1);
    linje.beginPath();
    snittsnitt.forEach((s, i) => {
      const px = grafX + (grafW * i) / Math.max(1, snittsnitt.length - 1);
      const py = grafY + grafH - ((s.kroner - min) / span) * grafH;
      if (i === 0) linje.moveTo(px, py);
      else linje.lineTo(px, py);
    });
    linje.strokePath();
    layer.add(linje);

    const aksLabelMaks = this.scene.add
      .text(grafX - 10, grafY, this.kompakt(maks), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#3a2e22',
      })
      .setOrigin(1, 0);
    layer.add(aksLabelMaks);

    const aksLabelMin = this.scene.add
      .text(grafX - 10, grafY + grafH, this.kompakt(min), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#3a2e22',
      })
      .setOrigin(1, 1);
    layer.add(aksLabelMin);

    const forste = snittsnitt[0];
    const siste = snittsnitt[snittsnitt.length - 1];
    const xStart = this.scene.add
      .text(grafX, grafY + grafH + 8, `dag ${forste.dag}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#3a2e22',
      })
      .setOrigin(0, 0);
    layer.add(xStart);

    const xEnd = this.scene.add
      .text(grafX + grafW, grafY + grafH + 8, `dag ${siste.dag}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#3a2e22',
      })
      .setOrigin(1, 0);
    layer.add(xEnd);

    return y + h;
  }

  private renderTopp10(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const topp = gameState.beregnTopp10Bestselgere();

    const tittel = this.scene.add
      .text(0, y, 'TOPP 10 BESTSELGERE', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(tittel);

    let cy = y + 40;

    if (topp.length === 0) {
      const tekst = this.scene.add
        .text(0, cy, 'Ingen utgitte bøker ennå', {
          fontFamily: FONTS.body,
          fontSize: fontStr(22),
          color: '#3a2e22',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0);
      layer.add(tekst);
      return cy + 40;
    }

    const radHoyde = 92;
    for (let i = 0; i < topp.length; i += 1) {
      const bok = topp[i];
      const radY = cy + i * (radHoyde + 8);

      const bg = this.scene.add
        .rectangle(0, radY, width, radHoyde, 0xe9d8a8, 0.55)
        .setOrigin(0, 0);
      layer.add(bg);

      const venstreBorder = this.scene.add
        .rectangle(0, radY, 4, radHoyde, COLORS.wine, 1)
        .setOrigin(0, 0);
      layer.add(venstreBorder);

      const nrTekst = this.scene.add
        .text(20, radY + radHoyde / 2, `${i + 1}.`, {
          fontFamily: FONTS.display,
          fontSize: fontStr(28),
          color: '#862736',
        })
        .setOrigin(0, 0.5);
      layer.add(nrTekst);

      const tittelT = this.scene.add
        .text(80, radY + 12, bok.title, {
          fontFamily: FONTS.display,
          fontSize: fontStr(24),
          color: '#1a1a1a',
          wordWrap: { width: width - 360 },
        })
        .setOrigin(0, 0);
      layer.add(tittelT);

      const forfatterT = this.scene.add
        .text(80, radY + radHoyde - 12, bok.author, {
          fontFamily: FONTS.body,
          fontSize: fontStr(16),
          color: '#3a2e22',
          fontStyle: 'italic',
        })
        .setOrigin(0, 1);
      layer.add(forfatterT);

      const salgT = this.scene.add
        .text(width - 20, radY + 12, `${bok.salg.toLocaleString('nb-NO')} solgte`, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(20),
          color: '#1a1a1a',
          fontStyle: '700',
        })
        .setOrigin(1, 0);
      layer.add(salgT);

      const snitt = snittTerninger(bok.anmeldelser);
      const snittTxt = bok.anmeldelser.length > 0
        ? `${snitt.toFixed(1)} · ${bok.anmeldelser.length} anm.`
        : 'ingen anm.';
      const snittT = this.scene.add
        .text(width - 20, radY + radHoyde - 12, snittTxt, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: '#3a2e22',
        })
        .setOrigin(1, 1);
      layer.add(snittT);
    }

    return cy + topp.length * (radHoyde + 8);
  }

  private renderSjangerFordeling(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const fordeling = gameState.beregnSjangerFordeling();
    const sjangre = Object.keys(fordeling).sort(
      (a, b) => fordeling[b] - fordeling[a],
    );

    const tittel = this.scene.add
      .text(0, y, 'SJANGER-FORDELING', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(tittel);

    let cy = y + 40;

    if (sjangre.length === 0) {
      const tekst = this.scene.add
        .text(0, cy, 'Ingen utgivelser å fordele', {
          fontFamily: FONTS.body,
          fontSize: fontStr(22),
          color: '#3a2e22',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0);
      layer.add(tekst);
      return cy + 40;
    }

    const total = sjangre.reduce((s, k) => s + fordeling[k], 0);
    const radHoyde = 42;
    const labelBredde = 200;
    const tallBredde = 80;
    const barX = labelBredde + 16;
    const barMaksW = width - labelBredde - tallBredde - 32;

    for (let i = 0; i < sjangre.length; i += 1) {
      const sjanger = sjangre[i];
      const antall = fordeling[sjanger];
      const farge = SJANGER_FARGER[sjanger] ?? SJANGER_FALLBACK_FARGE;
      const radY = cy + i * radHoyde;

      const label = this.scene.add
        .text(0, radY + radHoyde / 2, sjanger, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: '#1a1a1a',
          fontStyle: '700',
        })
        .setOrigin(0, 0.5);
      layer.add(label);

      const barBg = this.scene.add
        .rectangle(barX, radY + radHoyde / 2, barMaksW, 18, COLORS.beigeDark, 1)
        .setOrigin(0, 0.5);
      layer.add(barBg);

      const frac = total > 0 ? antall / total : 0;
      const barW = Math.max(4, Math.round(barMaksW * frac));
      const bar = this.scene.add
        .rectangle(barX, radY + radHoyde / 2, barW, 18, farge, 1)
        .setOrigin(0, 0.5);
      layer.add(bar);

      const antTxt = this.scene.add
        .text(width, radY + radHoyde / 2, `${antall}`, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: '#1a1a1a',
          fontStyle: '700',
        })
        .setOrigin(1, 0.5);
      layer.add(antTxt);
    }

    return cy + sjangre.length * radHoyde;
  }

  private renderPrisArkiv(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const tittel = this.scene.add
      .text(0, y, 'PRIS-ARKIV', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(tittel);

    let cy = y + 40;

    cy = this.renderPrisFilterPiller(layer, width, cy);
    cy += 16;

    type RadOppfoering = {
      kilde: 'brage' | PrisId;
      prisNavn: string;
      kategoriNavn: string;
      tittel: string;
      forfatterNavn: string;
      forlag: string;
      aar: number;
    };

    const rader: RadOppfoering[] = [];

    if (this.prisFilter === 'alle' || this.prisFilter === 'brage') {
      for (const v of gameState.brageHistorikk) {
        rader.push({
          kilde: 'brage',
          prisNavn: 'Brage',
          kategoriNavn: KATEGORI_NAVN[v.kategori],
          tittel: v.tittel,
          forfatterNavn: v.forfatterNavn,
          forlag: v.forlag,
          aar: 0,
        });
      }
    }

    for (const v of gameState.prisHistorikk) {
      if (this.prisFilter !== 'alle' && this.prisFilter !== v.prisId) continue;
      const pris = PRIS_KATALOG.find((p) => p.id === v.prisId);
      const kat = pris?.kategorier.find((k) => k.id === v.kategori);
      rader.push({
        kilde: v.prisId,
        prisNavn: pris?.navn ?? v.prisNavn,
        kategoriNavn: kat?.navn ?? v.kategori,
        tittel: v.bokTittel,
        forfatterNavn: v.forfatterNavn,
        forlag: v.forlag,
        aar: v.aar,
      });
    }

    if (rader.length === 0) {
      const tom = this.scene.add
        .text(
          0,
          cy,
          'Forlaget har ikke vunnet litterære priser ennå. Bygg gode bøker.',
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(22),
            color: '#3a2e22',
            fontStyle: 'italic',
            wordWrap: { width },
          },
        )
        .setOrigin(0, 0);
      layer.add(tom);
      return cy + 40;
    }

    const radH = 110;
    for (let i = 0; i < rader.length; i += 1) {
      const r = rader[i];
      const erSpiller = r.forlag === 'spiller';
      const radY = cy + i * (radH + 8);

      const bg = this.scene.add
        .rectangle(0, radY, width, radH, erSpiller ? 0xb8860b : 0xe9d8a8, erSpiller ? 0.18 : 0.5)
        .setOrigin(0, 0);
      layer.add(bg);

      const venstre = this.scene.add
        .rectangle(0, radY, 6, radH, erSpiller ? 0xb8860b : 0xd9a14b, 1)
        .setOrigin(0, 0);
      layer.add(venstre);

      const prisLabel = this.scene.add
        .text(20, radY + 10, r.prisNavn.toUpperCase(), {
          fontFamily: FONTS.hud,
          fontSize: fontStr(12),
          color: '#8b5e3c',
          fontStyle: '700',
        })
        .setOrigin(0, 0);
      layer.add(prisLabel);

      const tittelT = this.scene.add
        .text(20, radY + 42, r.tittel, {
          fontFamily: FONTS.display,
          fontSize: fontStr(20),
          color: erSpiller ? '#862736' : '#1a1a1a',
          wordWrap: { width: width - 320 },
        })
        .setOrigin(0, 0);
      layer.add(tittelT);

      const forlagT = erSpiller ? 'DITT FORLAG' : r.forlag;
      const meta = this.scene.add
        .text(20, radY + radH - 12, `${r.forfatterNavn} · ${forlagT}`, {
          fontFamily: FONTS.body,
          fontSize: fontStr(13),
          color: erSpiller ? '#862736' : '#3a2e22',
          fontStyle: erSpiller ? '700' : 'italic',
        })
        .setOrigin(0, 1);
      layer.add(meta);

      const kat = this.scene.add
        .text(width - 16, radY + radH / 2, r.kategoriNavn, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(14),
          color: '#8b5e3c',
          fontStyle: '700',
        })
        .setOrigin(1, 0.5);
      layer.add(kat);
    }
    return cy + rader.length * (radH + 8);
  }

  private renderInternasjonaleAvtaler(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const tittel = this.scene.add
      .text(0, y, 'INTERNASJONALE AVTALER', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(tittel);

    let cy = y + 40;

    const avtaler = [...gameState.internasjonaleAvtaler];
    if (avtaler.length === 0) {
      const tom = this.scene.add
        .text(0, cy, 'Ingen oversettelses-avtaler ennå.', {
          fontFamily: FONTS.body,
          fontSize: fontStr(22),
          color: '#3a2e22',
          fontStyle: 'italic',
          wordWrap: { width },
        })
        .setOrigin(0, 0);
      layer.add(tom);
      return cy + 40;
    }

    avtaler.sort((a, b) => {
      const totA = (a.engangsBelop ?? 0) + (a.totalRoyaltyMottatt ?? 0);
      const totB = (b.engangsBelop ?? 0) + (b.totalRoyaltyMottatt ?? 0);
      return totB - totA;
    });

    const radH = 110;
    for (let i = 0; i < avtaler.length; i += 1) {
      const a = avtaler[i];
      const radY = cy + i * (radH + 8);
      const totalInntekt = (a.engangsBelop ?? 0) + (a.totalRoyaltyMottatt ?? 0);

      const bg = this.scene.add
        .rectangle(0, radY, width, radH, 0xb8860b, 0.14)
        .setOrigin(0, 0);
      layer.add(bg);

      const venstre = this.scene.add
        .rectangle(0, radY, 6, radH, 0xb8860b, 1)
        .setOrigin(0, 0);
      layer.add(venstre);

      const flaggIkon = a.flagg ?? '↗';
      const flaggT = this.scene.add
        .text(20, radY + radH / 2, flaggIkon, {
          fontFamily: FONTS.display,
          fontSize: fontStr(28),
          color: '#1a1a1a',
        })
        .setOrigin(0, 0.5);
      layer.add(flaggT);

      const venstreTekstX = 76;
      const tittelT = this.scene.add
        .text(venstreTekstX, radY + 14, a.tittel, {
          fontFamily: FONTS.display,
          fontSize: fontStr(20),
          color: '#862736',
          wordWrap: { width: width - 360 },
        })
        .setOrigin(0, 0);
      layer.add(tittelT);

      const forlagDel = a.forlagNavn ? `${a.forlagNavn} · ` : '';
      const meta = this.scene.add
        .text(
          venstreTekstX,
          radY + radH - 14,
          `${forlagDel}${a.forfatterNavn} · ${a.sprak}`,
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(13),
            color: '#3a2e22',
            fontStyle: 'italic',
            wordWrap: { width: width - 280 },
          },
        )
        .setOrigin(0, 1);
      layer.add(meta);

      const inntektT = this.scene.add
        .text(
          width - 16,
          radY + 12,
          `${totalInntekt.toLocaleString('nb-NO')} kr totalt`,
          {
            fontFamily: FONTS.hud,
            fontSize: fontStr(15),
            color: '#8b5e3c',
            fontStyle: '700',
          },
        )
        .setOrigin(1, 0);
      layer.add(inntektT);

      const undertekst: string[] = [];
      if (a.engangsBelop && a.engangsBelop > 0) {
        undertekst.push(`engangs ${a.engangsBelop.toLocaleString('nb-NO')}`);
      }
      if (a.royaltyPerSalg && a.royaltyPerSalg > 0) {
        undertekst.push(`${a.royaltyPerSalg} kr/salg`);
      }
      if (a.totalRoyaltyMottatt && a.totalRoyaltyMottatt > 0) {
        undertekst.push(
          `royalty ${a.totalRoyaltyMottatt.toLocaleString('nb-NO')}`,
        );
      }
      const undertekstLinje =
        undertekst.length > 0 ? undertekst.join(' · ') : `dag ${a.dag}`;
      const undertekstT = this.scene.add
        .text(width - 16, radY + radH - 12, undertekstLinje, {
          fontFamily: FONTS.body,
          fontSize: fontStr(12),
          color: '#3a2e22',
          fontStyle: 'italic',
        })
        .setOrigin(1, 1);
      layer.add(undertekstT);
    }
    return cy + avtaler.length * (radH + 8);
  }

  private renderLydbokOgFilm(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const tittel = this.scene.add
      .text(0, y, 'LYDBOK OG FILM', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#3d5a4d',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(tittel);

    let cy = y + 40;

    const lydbok = gameState.lydbokAvtaler;
    const film = gameState.filmRettigheter;

    const undertittelLydbok = this.scene.add
      .text(0, cy, 'Aktive lydbok-avtaler', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#3a2e22',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(undertittelLydbok);
    cy += 30;

    if (lydbok.length === 0) {
      const tom = this.scene.add
        .text(0, cy, 'Ingen aktive lydbok-avtaler.', {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#3a2e22',
          fontStyle: 'italic',
          wordWrap: { width },
        })
        .setOrigin(0, 0);
      layer.add(tom);
      cy += 36;
    } else {
      const radH = 110;
      for (let i = 0; i < lydbok.length; i += 1) {
        const a = lydbok[i];
        const radY = cy + i * (radH + 8);
        const dagerIgjen = Math.max(0, a.ferdigDag - gameState.snapshot.dag);
        const dagerGaatt = Math.min(
          Math.max(0, gameState.snapshot.dag - a.startDag),
          a.ferdigDag - a.startDag,
        );
        const totalRoyalty = a.dagligRoyalty * dagerGaatt;
        const totalInntekt = a.engangsBelop + totalRoyalty;

        const bg = this.scene.add
          .rectangle(0, radY, width, radH, 0x3d5a4d, 0.14)
          .setOrigin(0, 0);
        layer.add(bg);

        const venstre = this.scene.add
          .rectangle(0, radY, 6, radH, 0x3d5a4d, 1)
          .setOrigin(0, 0);
        layer.add(venstre);

        const tittelT = this.scene.add
          .text(20, radY + 16, `♫ ${a.tjenesteNavn} · ${a.bokTittel}`, {
            fontFamily: FONTS.display,
            fontSize: fontStr(18),
            color: '#3d5a4d',
            wordWrap: { width: width - 240 },
          })
          .setOrigin(0, 0);
        layer.add(tittelT);

        const meta = this.scene.add
          .text(
            20,
            radY + radH - 14,
            `${dagerIgjen} dager igjen · ${a.dagligRoyalty.toLocaleString('nb-NO')} kr/dag · totalt ${totalInntekt.toLocaleString('nb-NO')} kr`,
            {
              fontFamily: FONTS.body,
              fontSize: fontStr(13),
              color: '#3a2e22',
              fontStyle: 'italic',
            },
          )
          .setOrigin(0, 1);
        layer.add(meta);
      }
      cy += lydbok.length * (radH + 8) + 16;
    }

    const undertittelFilm = this.scene.add
      .text(0, cy, 'Film-rettigheter', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#3a2e22',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(undertittelFilm);
    cy += 30;

    if (film.length === 0) {
      const tom = this.scene.add
        .text(0, cy, 'Ingen film-rettigheter solgt ennå.', {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#3a2e22',
          fontStyle: 'italic',
          wordWrap: { width },
        })
        .setOrigin(0, 0);
      layer.add(tom);
      cy += 36;
    } else {
      const radH = 110;
      for (let i = 0; i < film.length; i += 1) {
        const r = film[i];
        const radY = cy + i * (radH + 8);
        const statusTekst = filmStatusTekst(r, gameState.snapshot.dag);

        const bg = this.scene.add
          .rectangle(0, radY, width, radH, 0x4a3a5e, 0.14)
          .setOrigin(0, 0);
        layer.add(bg);

        const venstre = this.scene.add
          .rectangle(0, radY, 6, radH, 0x4a3a5e, 1)
          .setOrigin(0, 0);
        layer.add(venstre);

        const tittelT = this.scene.add
          .text(20, radY + 16, `✷ ${r.studioNavn} · ${r.bokTittel}`, {
            fontFamily: FONTS.display,
            fontSize: fontStr(18),
            color: '#4a3a5e',
            wordWrap: { width: width - 240 },
          })
          .setOrigin(0, 0);
        layer.add(tittelT);

        const meta = this.scene.add
          .text(20, radY + radH - 14, statusTekst, {
            fontFamily: FONTS.body,
            fontSize: fontStr(13),
            color: '#3a2e22',
            fontStyle: 'italic',
            wordWrap: { width: width - 240 },
          })
          .setOrigin(0, 1);
        layer.add(meta);
      }
      cy += film.length * (radH + 8);
    }

    return cy;
  }

  private renderCalidrisAvkortninger(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const avkortninger = gameState.calidrisAvkortninger;
    if (avkortninger.length === 0) return y;

    const tittel = this.scene.add
      .text(0, y, 'CALIDRIS-AVKORTNINGER', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(tittel);
    let cy = y + 40;

    const undertittel = this.scene.add
      .text(0, cy, 'Calidris Forlag', {
        fontFamily: FONTS.display,
        fontSize: fontStr(18),
        fontStyle: 'italic',
        color: '#3a2e22',
      })
      .setOrigin(0, 0);
    layer.add(undertittel);
    cy += 32;

    const alleBoker = [
      ...gameState.utgitteBoker,
      ...gameState.iProduksjon.filter((b) => b.status === 'utgitt'),
    ];
    const tittelKart = new Map<string, string>();
    for (const b of alleBoker) tittelKart.set(b.isbn, b.title);

    const radH = 84;
    for (let i = 0; i < avkortninger.length; i += 1) {
      const a = avkortninger[i];
      const radY = cy + i * (radH + 8);

      const bg = this.scene.add
        .rectangle(0, radY, width, radH, 0xb8860b, 0.14)
        .setOrigin(0, 0);
      layer.add(bg);

      const venstre = this.scene.add
        .rectangle(0, radY, 6, radH, 0xb8860b, 1)
        .setOrigin(0, 0);
      layer.add(venstre);

      const bokTittel = tittelKart.get(a.isbn) ?? a.isbn;
      const tittelT = this.scene.add
        .text(20, radY + 14, bokTittel, {
          fontFamily: FONTS.display,
          fontSize: fontStr(18),
          color: '#8b5e3c',
          wordWrap: { width: width - 200 },
        })
        .setOrigin(0, 0);
      layer.add(tittelT);

      const meta = this.scene.add
        .text(20, radY + radH - 12, 'tatt av Calidris Forlag', {
          fontFamily: FONTS.body,
          fontSize: fontStr(13),
          color: '#3a2e22',
          fontStyle: 'italic',
        })
        .setOrigin(0, 1);
      layer.add(meta);

      const prosentT = this.scene.add
        .text(width - 16, radY + radH / 2, `${a.prosentTilCalidris} %`, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(24),
          color: '#8b5e3c',
          fontStyle: '700',
        })
        .setOrigin(1, 0.5);
      layer.add(prosentT);
    }

    return cy + avkortninger.length * (radH + 8);
  }

  private renderPrisFilterPiller(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
  ): number {
    const piller: Array<{ id: PrisArkivFilter; label: string }> = [
      { id: 'alle', label: 'Alle priser' },
      { id: 'brage', label: 'Brage' },
      { id: 'bokhandlerprisen', label: 'Bokhandler' },
      { id: 'ungdommens', label: 'Ungdommens' },
      { id: 'p2-lytternes', label: 'P2-lytternes' },
    ];

    const pilleH = 48;
    const padX = 16;
    let cx = 0;
    let cy = y;
    const gap = 10;

    for (const p of piller) {
      const aktiv = this.prisFilter === p.id;
      const txt = this.scene.add
        .text(0, 0, p.label, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(14),
          color: aktiv ? '#ffffff' : '#3a2e22',
          fontStyle: '700',
        })
        .setOrigin(0, 0);
      const w = txt.width + padX * 2;

      if (cx + w > width) {
        cx = 0;
        cy += pilleH + gap;
      }

      const bg = this.scene.add
        .rectangle(cx, cy, w, pilleH, aktiv ? COLORS.wine : 0xd9a14b, aktiv ? 1 : 0.35)
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true });
      bg.on('pointerup', () => {
        this.prisFilter = p.id;
        this.render();
      });
      layer.add(bg);

      txt.setPosition(cx + padX, cy + (pilleH - txt.height) / 2);
      layer.add(txt);

      cx += w + gap;
    }
    return cy + pilleH;
  }

  private kompakt(n: number): string {
    const neg = n < 0;
    const abs = Math.abs(n);
    let str: string;
    if (abs >= 1_000_000) str = `${(abs / 1_000_000).toFixed(1)}M`;
    else if (abs >= 10_000) str = `${Math.round(abs / 1_000)}k`;
    else str = abs.toLocaleString('nb-NO');
    return neg ? `−${str}` : str;
  }
}

const SESONG_GRENSER: ReadonlyArray<{ slutt: number; sesong: Sesong }> = [
  { slutt: 20, sesong: 'vaar' },
  { slutt: 60, sesong: 'sommer' },
  { slutt: 80, sesong: 'bokhost' },
  { slutt: 100, sesong: 'jul' },
  { slutt: DAGER_PER_AAR, sesong: 'vinter' },
];

function nesteSesong(dag: number): Sesong {
  const d = dagIAaret(dag);
  for (const grense of SESONG_GRENSER) {
    if (d <= grense.slutt) {
      const idx = SESONG_GRENSER.indexOf(grense);
      const neste = SESONG_GRENSER[(idx + 1) % SESONG_GRENSER.length];
      return neste.sesong;
    }
  }
  return 'vaar';
}

function filmStatusTekst(r: FilmRettighet, dag: number): string {
  if (r.produseresStatus === 'lansert') {
    const dagerSiden = r.produseresFerdigDag !== null ? dag - r.produseresFerdigDag : 0;
    return `lansert · ${dagerSiden} dager siden · ${r.opsjonsBelop.toLocaleString('nb-NO')} kr opsjon`;
  }
  if (r.produseresStatus === 'ikke_produsert') {
    return `ikke produsert · ${r.opsjonsBelop.toLocaleString('nb-NO')} kr opsjon`;
  }
  if (r.produseresStatus === 'produseres' && r.produseresFerdigDag !== null) {
    const igjen = Math.max(0, r.produseresFerdigDag - dag);
    return `produseres · ${igjen} dager til avgjørelse · ${r.opsjonsBelop.toLocaleString('nb-NO')} kr opsjon`;
  }
  return `venter · ${r.opsjonsBelop.toLocaleString('nb-NO')} kr opsjon`;
}

function dagerTilSesongSkift(dag: number): number {
  const d = dagIAaret(dag);
  for (const grense of SESONG_GRENSER) {
    if (d <= grense.slutt) return grense.slutt - d + 1;
  }
  return DAGER_PER_AAR - d + 1;
}

function beregnStatSnitt(stats: import('@/data/types').AuthorStats): number {
  return (
    (stats.dialog +
      stats.plot +
      stats.karakter +
      stats.spraak +
      stats.voice +
      stats.autentisitet +
      stats.disiplin) /
    7
  );
}
